import React, { useState } from "react";
import * as finService from "../../../services/finService";
import MasterSection from "../../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "../useFinOptions";
import { DataTable, act, hint } from "../gl/glUi";
import { FormDrawer, LoadState, SecondaryButton, formatDate, formatDateTime, useLoad } from "../../../components/ui";

// Finance Setup tabs: which core modules post to the ledger (bookings, operating expenses,
// payroll), and the fixed asset categories.

export function CoreSourcesSetup() {
  const { data, loading, error, reload } = useLoad(() => finService.listCoreSources(), []);
  const fin = useFinOptions(["locations"]);
  const [editing, setEditing] = useState(null);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div style={hint}>
          Switched-on sources are read every 10 minutes; each document is accounted once (a change books only the difference, a void reverses it). Inventory books each stock movement as it happens, at the
          warehouse's Finance location (else the one below); open the INV application in Periods.
        </div>
        <span className="d-flex gap-2">
          {(data || []).some((r) => r.source === "INVENTORY" && r.is_enabled) ? (
            <SecondaryButton onClick={() => window.confirm("Book the stock's value today as opening inventory (Dr inventory / Cr opening balance equity)? This is done once.") && act(() => finService.postOpeningInventory()).then(reload)}>Book opening inventory</SecondaryButton>
          ) : null}
          <SecondaryButton onClick={() => act(() => finService.runCoreSources()).then(reload)}>Run now</SecondaryButton>
        </span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          rowKey={(r) => r.source}
          onRowClick={(r) => setEditing(r)}
          columns={[
            { label: "Source", render: (r) => <b>{r.label}</b> },
            { label: "On", render: (r) => (r.is_enabled ? "Yes" : "No") },
            { label: "From", render: (r) => (r.from_date ? formatDate(r.from_date) : "—") },
            { label: "Posts to", render: (r) => (r.location_code ? `${r.location_code} – ${r.location_name}` : "—") },
            { label: "Last run", render: (r) => (r.last_run_at ? formatDateTime(r.last_run_at) : "—") },
            { label: "Problem", render: (r) => <span style={{ color: "#B91C1C" }}>{r.last_error || ""}</span> },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!editing}
        title={editing ? editing.label : ""}
        initial={editing ? { is_enabled: !!editing.is_enabled, from_date: editing.from_date ? String(editing.from_date).slice(0, 10) : "", location_id: optionByCode(fin.locations, editing.location_code) } : {}}
        onHide={() => setEditing(null)}
        onDone={reload}
        onSave={(clean) => finService.saveCoreSource(editing.source, clean)}
        fields={[
          { key: "is_enabled", label: "Post this module's documents to the ledger", type: "checkbox" },
          { key: "from_date", label: "From (go-live date)", type: "date", required: true, hint: "Documents before it stay out (bring their balances in as opening balances)" },
          { key: "location_id", label: "Post to location (company / BU / cost centre)", type: "select", required: true, options: fin.locations || [] },
        ]}
      />
    </>
  );
}

const METHODS = [
  { label: "Straight line", value: "SL" },
  { label: "Declining balance", value: "DB" },
];

export function AssetCategoriesSetup() {
  return (
    <MasterSection
      load={() => finService.listMaster("asset-categories")}
      save={(id, d) => finService.saveMaster("asset-categories", id, d)}
      noun="asset category"
      columns={[
        ["Code", (r) => r.code],
        ["Name", (r) => r.name],
        ["Method", (r) => (r.method === "DB" ? `Declining ${Number(r.db_rate_pct)}%` : "Straight line")],
        ["Life", (r) => `${r.life_months} months`],
        ["Accounts", (r) => [r.cost_account, r.accum_account, r.expense_account].filter(Boolean).join(" / ") || "Defaults"],
      ]}
      fieldKeys={["name", "method", "life_months", "db_rate_pct", "salvage_pct", "cost_account", "accum_account", "expense_account"]}
      lockedOnEdit={["code"]}
      defaults={{ method: "SL", salvage_pct: 0 }}
      fields={(v) => [
        { key: "code", label: "Code", required: true, max: 24 },
        { key: "name", label: "Name", required: true, max: 100 },
        { key: "method", label: "Method", type: "select", required: true, options: METHODS },
        { key: "life_months", label: "Useful life (months)", type: "number", min: 1, required: true },
        ...(v.method === "DB" ? [{ key: "db_rate_pct", label: "Rate % a year", type: "number", step: "any", min: 0, required: true }] : []),
        { key: "salvage_pct", label: "Salvage % of cost", type: "number", step: "any", min: 0 },
        { key: "cost_account", label: "Cost account (blank: default)", max: 25 },
        { key: "accum_account", label: "Accumulated depreciation account (blank: default)", max: 25 },
        { key: "expense_account", label: "Depreciation expense account (blank: default)", max: 25 },
      ]}
    />
  );
}
